import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Loader2, Lock, PlusIcon, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { MACHINERY_DIRECTIVE_STUDIO_ID } from '@artem-lex/common-server/complianceStudio'
import {
  buildStandardApplicabilityDecisionPatches,
  type StandardApplicability,
  useBatchUpdateStandardApplicabilityDecisions,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/shared/lib/utils'
import { AddStandardModal } from '../steps/standards/AddStandardModal'
import {
  isTypeCStandard,
} from '../utils/standardKinds'
import {
  applySelectAllWithLocks,
  reconcileSelectedIdsWithLocks,
  toggleSelectedIdWithLocks,
} from '../utils/onboardingStandardsSelection'
import { sortStandardsForTypeCReview } from '../utils/sortOnboardingStandards'
import { getLockedTypeAIds, getRetainedTypeAIdsOnDeselectAll } from '../utils/typeAStandardGuard'

export type OnboardingCStandardsReviewTableProps = {
  standards: StandardApplicability[]
  complianceId: string
  /** Draft selection lifted above the step so Back/forward keeps local edits. */
  selectedIds: Set<string>
  onSelectedIdsChange: (
    next:
      | Set<string>
      | ((previous: Set<string>) => Set<string>)
  ) => void
  onComplete: () => void
  onRerunTypeCStandardCheck: () => void
  isTypeCStandardCheckRunning: boolean
}

export function OnboardingCStandardsReviewTable({
  standards,
  complianceId,
  selectedIds,
  onSelectedIdsChange,
  onComplete,
  onRerunTypeCStandardCheck,
  isTypeCStandardCheckRunning,
}: OnboardingCStandardsReviewTableProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const batchMutation =
    useBatchUpdateStandardApplicabilityDecisions(complianceId)

  const sortedStandards = useMemo(
    () => sortStandardsForTypeCReview(standards),
    [standards]
  )
  const [zeroSelectionConfirmOpen, setZeroSelectionConfirmOpen] =
    useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isAddStandardModalOpen, setIsAddStandardModalOpen] = useState(false)

  const lockedTypeAIds = useMemo(
    () => getLockedTypeAIds(sortedStandards, selectedIds),
    [sortedStandards, selectedIds]
  )
  const retainedTypeAIdsOnDeselectAll = useMemo(
    () => getRetainedTypeAIdsOnDeselectAll(sortedStandards, selectedIds),
    [sortedStandards, selectedIds]
  )

  // Reconcile only when the standards list changes. Do not depend on
  // lockedTypeAIds here — it is derived from selectedIds, and pairing that with
  // onSelectedIdsChange caused an infinite update loop.
  useEffect(() => {
    const currentStandardIds = sortedStandards.map((standard) => standard.id)
    onSelectedIdsChange((previous) => {
      const lockedIds = getLockedTypeAIds(sortedStandards, previous)
      return reconcileSelectedIdsWithLocks(
        previous,
        currentStandardIds,
        lockedIds
      )
    })
  }, [sortedStandards, onSelectedIdsChange])

  const totalCount = sortedStandards.length
  const selectedCount = selectedIds.size
  const typeCStandards = useMemo(
    () => sortedStandards.filter((standard) => isTypeCStandard(standard.type)),
    [sortedStandards]
  )
  const selectedTypeCCount = useMemo(
    () =>
      typeCStandards.filter((standard) => selectedIds.has(standard.id)).length,
    [typeCStandards, selectedIds]
  )
  const allSelected =
    totalCount > 0 &&
    sortedStandards
      .filter((row) => !lockedTypeAIds.has(row.id))
      .every((row) => selectedIds.has(row.id))

  const toggleSelected = useCallback((standardId: string, checked: boolean) => {
    onSelectedIdsChange((previous) => {
      return toggleSelectedIdWithLocks(
        previous,
        standardId,
        checked,
        lockedTypeAIds
      )
    })
  }, [lockedTypeAIds, onSelectedIdsChange])

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      onSelectedIdsChange(
        applySelectAllWithLocks(
          checked,
          sortedStandards.map((row) => row.id),
          lockedTypeAIds,
          retainedTypeAIdsOnDeselectAll
        )
      )
    },
    [
      sortedStandards,
      lockedTypeAIds,
      retainedTypeAIdsOnDeselectAll,
      onSelectedIdsChange,
    ]
  )

  const submitDecisions = useCallback(() => {
    setSubmitError(null)
    const patches = buildStandardApplicabilityDecisionPatches(
      sortedStandards,
      selectedIds
    )
    Sentry.addBreadcrumb({
      category: 'sa1.type_c_selection',
      level: 'info',
      message: 'continue',
      data: {
        complianceId,
        selectedTypeCCount,
        totalTypeCCount: typeCStandards.length,
        selectedCount,
        totalCount,
      },
    })
    batchMutation.mutate(patches, {
      onSuccess: () => {
        setZeroSelectionConfirmOpen(false)
        onComplete()
      },
      onError: () => {
        setSubmitError(t('onboarding.typeCStandardsReviewTable.submitError'))
      },
    })
  }, [
    batchMutation,
    complianceId,
    onComplete,
    selectedIds,
    selectedTypeCCount,
    selectedCount,
    sortedStandards,
    t,
    totalCount,
    typeCStandards.length,
  ])

  const handleContinueClick = useCallback(() => {
    if (selectedTypeCCount === 0) {
      Sentry.addBreadcrumb({
        category: 'sa1.type_c_selection',
        level: 'info',
        message: 'continue_without_type_c_prompted',
        data: { complianceId },
      })
      setZeroSelectionConfirmOpen(true)
      return
    }
    submitDecisions()
  }, [complianceId, selectedTypeCCount, submitDecisions])

  const isSubmitting = batchMutation.isPending
  const isBusy = isSubmitting || isTypeCStandardCheckRunning

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col'>
        <div className='flex min-h-0 flex-1 flex-col gap-6'>
          <div className='space-y-4'>
            <h2 className='text-lg font-semibold tracking-tight'>
              {t('onboarding.typeCStandardsReviewTable.title')}
            </h2>
            <p className='leading-relaxed text-muted-foreground'>
              {t('onboarding.typeCStandardsReviewTable.intro')}
            </p>
            <div className='flex flex-wrap gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setIsAddStandardModalOpen(true)}
                disabled={isBusy}
              >
                <PlusIcon className='mr-2 size-4' />
                {t('onboarding.typeCStandardsReviewTable.addTypeCButton')}
              </Button>
              <Button
                type='button'
                variant='outline'
                onClick={onRerunTypeCStandardCheck}
                disabled={isBusy}
              >
                {isTypeCStandardCheckRunning ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <RefreshCw className='mr-2 size-4' />
                )}
                {t('onboarding.typeCStandardsReviewTable.rerunTypeCStandardCheckButton')}
              </Button>
            </div>
          </div>

          {typeCStandards.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              {t('onboarding.typeCStandardsReviewTable.noTypeCStandardsFound')}
            </p>
          )}

          <div className='flex items-center gap-2 border-b pb-3'>
            <Checkbox
              checked={allSelected}
              onCheckedChange={(checked) => {
                toggleSelectAll(checked === true)
              }}
              aria-label={t('onboarding.typeCStandardsReviewTable.selectAllAria')}
              disabled={totalCount === 0 || isBusy}
            />
            <span className='text-sm text-muted-foreground'>
              {t('onboarding.typeCStandardsReviewTable.selectAllAria')}
            </span>
          </div>

          <div className='space-y-4'>
            {sortedStandards.map((standard) => {
            const isSelected = selectedIds.has(standard.id)
            const isLockedTypeA = lockedTypeAIds.has(standard.id)
            const keepStandardLabel = t(
              'onboarding.typeCStandardsReviewTable.keepStandardAria',
              { reference: standard.reference }
            )
            const cardContent = (
              <>
                <Checkbox
                  checked={isSelected}
                  disabled={isBusy || isLockedTypeA}
                  tabIndex={-1}
                  aria-hidden
                  className='pointer-events-none mt-1'
                />
                <div className='min-w-0 flex-1 space-y-1'>
                  <p className='font-medium text-foreground'>{standard.title}</p>
                  <div className='flex flex-wrap items-center gap-2'>
                    <p className='text-sm text-muted-foreground'>
                      {standard.reference}
                    </p>
                    {isLockedTypeA && (
                      <Badge variant='secondary' className='gap-1'>
                        <Lock className='size-3' />
                        {t('onboarding.typeCStandardsReviewTable.typeALastLocked')}
                      </Badge>
                    )}
                  </div>
                  {standard.explanation?.trim() ? (
                    <p className='text-sm leading-relaxed text-muted-foreground'>
                      {standard.explanation}
                    </p>
                  ) : null}
                </div>
              </>
            )

            if (isLockedTypeA || isBusy) {
              return (
                <div
                  key={standard.id}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-md border p-4 text-left',
                    isSelected && 'border-primary/50 bg-primary/5'
                  )}
                  aria-label={keepStandardLabel}
                >
                  {cardContent}
                </div>
              )
            }

            return (
              <button
                key={standard.id}
                type='button'
                aria-pressed={isSelected}
                aria-label={keepStandardLabel}
                onClick={() => {
                  toggleSelected(standard.id, !isSelected)
                }}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md border p-4 text-left transition-colors',
                  'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isSelected && 'border-primary/50 bg-primary/5'
                )}
              >
                {cardContent}
              </button>
            )
          })}
          </div>

          {submitError && (
            <p className='text-sm text-destructive' role='alert'>
              {submitError}
            </p>
          )}
        </div>

        <div className='mt-6 flex shrink-0 flex-wrap items-center justify-between gap-4 border-t pt-6'>
          <p className='text-sm text-muted-foreground'>
            {t('onboarding.typeCStandardsReviewTable.selectedTypeCCount', {
              selected: selectedTypeCCount,
              total: typeCStandards.length,
            })}
          </p>
          <Button onClick={handleContinueClick} disabled={isBusy}>
            {isSubmitting && <Loader2 className='mr-2 size-4 animate-spin' />}
            {t('onboarding.typeCStandardsReviewTable.continue')}
          </Button>
        </div>
      </div>

      <AlertDialog
        open={zeroSelectionConfirmOpen}
        onOpenChange={setZeroSelectionConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('onboarding.typeCStandardsReviewTable.zeroSelectionConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('onboarding.typeCStandardsReviewTable.zeroSelectionConfirmBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t('onboarding.typeCStandardsReviewTable.zeroSelectionConfirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isSubmitting}
              onClick={(event) => {
                event.preventDefault()
                Sentry.addBreadcrumb({
                  category: 'sa1.type_c_selection',
                  level: 'info',
                  message: 'continue_without_type_c_confirmed',
                  data: { complianceId },
                })
                submitDecisions()
              }}
            >
              {t(
                'onboarding.typeCStandardsReviewTable.zeroSelectionConfirmContinue'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AddStandardModal
        isOpen={isAddStandardModalOpen}
        onClose={() => setIsAddStandardModalOpen(false)}
        onAdded={(applicabilityId) => {
          Sentry.addBreadcrumb({
            category: 'sa1.type_c_inline_add',
            level: 'info',
            message: 'added_type_c',
            data: { complianceId, applicabilityId },
          })
          onSelectedIdsChange((previous) => {
            const next = new Set(previous)
            next.add(applicabilityId)
            return next
          })
        }}
        onDuplicateReference={(existingApplicabilityId) => {
          Sentry.addBreadcrumb({
            category: 'sa1.type_c_inline_add',
            level: 'info',
            message: 'duplicate_type_c',
            data: { complianceId, applicabilityId: existingApplicabilityId },
          })
          onSelectedIdsChange((previous) => {
            const next = new Set(previous)
            next.add(existingApplicabilityId)
            return next
          })
          toast.success(
            t('onboarding.typeCStandardsReviewTable.duplicateTypeCAddedInfo')
          )
        }}
        complianceId={complianceId}
        implicitComplianceStudioId={MACHINERY_DIRECTIVE_STUDIO_ID}
        allowedStandardTypes={['C']}
        allowCreateNew={false}
      />
    </>
  )
}
